import { Injectable } from '@angular/core';
import { CompanyService } from '@company/feature';
import { Store } from '@ngrx/store';
import { AuthService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user';
import { CurrentProfile } from '@user/const';
import { CustomUserActions, CustomUserSelectors, StateWithCustomUser } from '@user/data-access';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { LoyaltyProgramService } from './loyalty-program.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentProfileService {
  constructor(
    private authService: AuthService,
    private userAccountService: UserAccountFacade,
    private companyService: CompanyService,
    private store: Store<StateWithCustomUser>,
    private loyaltyProgramService: LoyaltyProgramService,
  ) {
    this.loyaltyProgramService.loadIfNeedBonusBalance();
  }

  get(): Observable<CurrentProfile> {
    return combineLatest({ isLoggedIn: this.authService.isUserLoggedIn(), isLegal: this.getLegalEntityStatus() }).pipe(
      switchMap(({ isLoggedIn, isLegal }) => {
        if (!isLoggedIn) {
          return of({
            isLoggedIn,
            isLegal,
          });
        }

        return combineLatest({
          user: this.userAccountService.get().pipe(filter(Boolean)),
          companies: this.companyService.getUserCompanies(),
          selectedCompany: this.companyService.getSelectedCompany(),
          bonusBalance: this.loyaltyProgramService.getBonusBalance(),
        }).pipe(
          map(({ user = {}, companies, selectedCompany = {}, bonusBalance }) => {
            const fullName: string = user.name || '';
            const userFirstName: string = user.name?.split(' ')[0] || '';

            return {
              isLoggedIn,
              isLegal,
              user,
              name: isLegal ? selectedCompany.name : userFirstName,
              fullName: isLegal ? selectedCompany.name : fullName,
              selectedCompanyUid: selectedCompany.uid,
              companies,
              bonusBalance,
            };
          }),
        );
      }),
    );
  }

  isForeman(): Observable<boolean> {
    return this.get().pipe(map((profile) => profile?.user?.additionalOptions?.isForeman ?? false));
  }

  getLegalEntityStatus(): Observable<boolean> {
    return this.store.select(CustomUserSelectors.getLegalEntityStatus);
  }

  setLegalEntityStatus(isLegal: boolean): void {
    this.store.dispatch(new CustomUserActions.SetLegalEntityStatus(isLegal));
  }
}
